/** HPA axis states */
import AAS_AXIS from "../assets/hpa-aas.png";
import CLOMID_LOW_AXIS from "../assets/hpa-clomid-low.png";
import CLOMID_HIGH_AXIS from "../assets/hpa-clomid-high.png";
import AIS_AXIS from "../assets/hpa-ais.png";
import NO_RETURN_AXIS from "../assets/hpa-no-return.png";
import HCG_AXIS from "../assets/hpa-hcg.png";

/** Lab risks */
export const NO_RISK = 0;
export const MILD_MAX = 25;
export const MED_MAX = 50;
export const MED2_MAX = 75;
export const HIGH_MAX = 100;

export const LOW_INC = 5;
export const MED_INC = 25;
export const HIGH_INC = 50;

/** Default state */
export const DEFAULT_STATE = {
  bioClock: 100,
  axis: {
    HPA_AAS: {
      overlay: AAS_AXIS,
      alt: "Getting testosterone exogeneously",
      count: 0
    },
    HPA_CLOMID_LOW: {
      overlay: CLOMID_LOW_AXIS,
      alt: "",
      count: 0
    },
    HPA_CLOMID_HIGH: {
      overlay: CLOMID_HIGH_AXIS,
      alt: "",
      count: 0
    },
    HPA_NO_AXIS: {
      overlay: NO_RETURN_AXIS,
      alt: "",
      count: 0
    },
    HPA_HCG: {
      overlay: HCG_AXIS,
      alt: "",
      count: 0
    },
    HPA_AIS: {
      overlay: AIS_AXIS,
      alt: "",
      count: 0
    }
  },
  history: [],
  prevStates: [],
  body: {
    general: {
      abnormality: HIGH_MAX, // always show
      risks: [],
      patientNextSteps: ["Body dysmorphia screening", "Fertility preservation/family planning"],
      providerNextSteps: ["Screen patient for body dysmorphia", "Discuss fertility preservation/family planning with patient"],
      name: "General",
    },
    bodyFat: {
      abnormality: MED2_MAX, // start with some body fat
      risks: [],
      patientNextSteps: [],
      providerNextSteps: [],
      name: "Body Fat",
    },
    muscleMass: {
      abnormality: NO_RISK,
      risks: [],
      patientNextSteps: [],
      providerNextSteps: [],
      name: "Muscle Mass",
    },
    pulmonaryEmbolism: {
      abnormality: NO_RISK,
      risks: ["Using Tamoxifen can put you at risk for pulmonary embolism."],
      patientNextSteps: [],
      providerNextSteps: [],
      name: "Pulmonary Embolism",
    },
    acne: {
      abnormality: NO_RISK,
      risks: ["Using steroids can increase acne."],
      patientNextSteps: ["See a dermatologist for acne medication."],
      providerNextSteps: ["Refer patient to a dermatologist for acne medication."],
      name: "Acne",
    },
    deepVeinThrombosis: {
      abnormality: NO_RISK,
      risks: ["Using Tamoxifen can put you at risk for deep vein thrombosis."],
      patientNextSteps: ["Avoid periods of prolonged immobility, such as long flights and car rides. Go for walks to improve blood circulation in your legs."],
      providerNextSteps: ["Consider DVT/PE in differential diagnosis as appropriate. The role of antiplatelet or anticoagulants is unclear for prevention of blood clots at this time."],
      name: "Deep Vein Thrombosis",
    },
    sexualDysfunction: {
      abnormality: NO_RISK,
      risks: ["Steroids and aromatase inhibitors can lead to sexual dysfunction. You may also experience prostate growth, causing difficulty urinating."],
      patientNextSteps: ["See your provider for treatment for sexual dysfunction.",
                         "We recommend you see a doctor about prostate screening and medications, such as tamsulosin, to help with benign prostate hypertrophy (BPH)."],
      providerNextSteps: ["Sexual dysfunction treatment", "Prostate screening and medications, such as tamsulosin, to help with benign prostate hypertrophy (BPH)."],
      name: "Sexual Dysfunction",
    },
    gynecomastia: {
      abnormality: NO_RISK,
      risks: ["Steroids or hCG can lead to increased breast tissue (gynecomastia)."],
      patientNextSteps: ["Aromatase inhibitors and SERMs can help decrease gynecomastia, but they have other risks and side effects as well. See your doctor for treatment."],
      providerNextSteps: ["Discuss risks/benefits of steroid use and consider use of an aromatase inhibitor and/or referral to plastic surgery."],
      name: "Increased Breast Tissue",
    },
    hair: {
      abnormality: NO_RISK,
      risks: ["Using steroids can cause hair loss."],
      patientNextSteps: ["See your provider for hair loss treatment."],
      providerNextSteps: ["Discuss minoxidil, finasteride, and other options for maintaining hair."],
      name: "Hair Loss",
    },
    visualDisturbances: {
      abnormality: NO_RISK,
      risks: ["SERMs can cause visual disturbances, like corneal deposits, cataract, epithelial keratopathy, macular edema, maculopathy, optic neuritis, retinal thrombosis, retinopathy, and vision color changes."],
      patientNextSteps: ["See your provider for treatment."],
      providerNextSteps: [], // TODO TREATMENT HOW?
      name: "Visual Disturbances",
    },
    headache: {
      abnormality: NO_RISK,
      risks: ["SERMs can cause headaches."],
      patientNextSteps: ["See your provider for treatment."],
      providerNextSteps: [], // TODO TREATMENT HOW?
      name: "Headache",
    },
    infectionRisk: {
      abnormality: NO_RISK,
      risks: ["Using injectables increases your risk of of cellulitis (infection of the skin and soft tissue), abscesses, HIV, Hep C, and other bloodborne and needle related infections."],
      patientNextSteps: [<span>
                            Ask provider about getting access to clean needles & videos on safe injecting techniques.
                            See <a href="https://harmreduction.org/issues/safer-drug-use/facts/">this page</a> for more
                            techniques on how to inject safely.
                          </span>,
                          "Ensure your TDAP vaccine is up to date."
                          ],
      providerNextSteps: [<span>
                            Ensure patient has access to clean needles & videos on safe injecting techniques.
                            See <a href="https://harmreduction.org/issues/safer-drug-use/facts/">this page</a> for more
                            techniques on how to inject safely.
                          </span>,
                          "Ensure patient's TDAP vaccine is up to date."],
      name: "Infection Risk",
    },
    mood: {
      abnormality: NO_RISK,
      risks: ["Taking steroids can lead to mood disorders, such as worsening depression, anxiety, and anger. You may also experience increased sexual desire, impulsivity, and difficulties managing anger."],
      patientNextSteps: ["See a behavioral therapist for treatment."],
      providerNextSteps: ["Refer patient to a behavioral therapist for treatment."],
      name: "Mood",
    },
    strokeRisk: {
      abnormality: NO_RISK,
      risks: ["Steroids increase blood counts, which increase the risk of strokes."],
      patientNextSteps: ["Check your complete blood count (CBC) for your hemoglobin. If elevated, consider donating blood and/or stopping steroids. If you notice any new neurological symptoms, immediately present to care."],
      providerNextSteps: ["Screen CBC and recommend routine phlebotomy for patients with elevated hematocrit. Some clinicians recommend using aspirin as a means of stroke prevention, but limited data exist. Optimize traditional stroke-related risk factors, such as cholesterol, diabetes, and smoking."],
      name: "Stroke Risk",
    },
    tendonRupture: {
      abnormality: NO_RISK,
      risks: ["Using steroids increases your risk of tendon ruptures."],
      patientNextSteps: ["Start low and go slow–when exercising, take precaution to not overdo it. If something feels unusual, seek medical care."],
      providerNextSteps: ["Counsel on importance of safe exercise practices."],
      name: "Tendon Rupture",
    },
    vasomotorSymptoms: {
      abnormality: NO_RISK,
      risks: ["Tamoxifen can cause hot flashes and night sweats."],
      patientNextSteps: ["Wear layers to easily adjust with temperature changes, and use cool compresses during hot flashes."],
      providerNextSteps: ["Discuss risk/benefit profile of ongoing tamoxifen use. Can consider prescriptions for symptomatic relief."],
      name: "Vasomotor Symptoms",
    }
  },
  bloodwork: {
    bmp: {
      name: "Basic Metabolic Panel",
      info: "Screening for electrolyte abnormalities, kidney damage (elevated creatinine)",
      abnormality: NO_RISK,
      risks: [
        "Kidney damage (elevated creatinine)",
        "Electrolyte abnormalities"
      ],
      patientNextSteps: [
        "Hold off on significant physical activity",
        "Stop taking any non-prescribed diuretics",
        "Stop all non-prescribed supplements and non-prescribed over-the-counter medications",
        "Monitor for any new symptoms, especially palpitations, loss of consciousness, and decreased urine"
      ],
      providerNextSteps: [
        "Recommend that patient holds off on significant physical activity",
        "Encourage patient to stop taking any non-prescribed diuretics",
        "Encourage patient to stop all non-prescribed supplements and non-prescribed over-the-counter medications",
        "Monitor patient for any new symptoms, especially palpitations, loss of consciousness, and decreased urine"
      ]
    },
    calciumScore: {
      name: "Calcium Score",
      info: "Screening for evidence of coronary artery disease",
      abnormality: NO_RISK,
      risks: [
        "Coronary artery disease"
      ],
      patientNextSteps: [
        "Strongly consider stopping AAS use",
        "Strongly consider stopping all non-prescribed aromatase inhibitors, SERMs, supplements, and non-prescribed over-the-counter medications",
        "Discuss with your doctor about starting cholesterol lowering medications",
        "Decrease cholesterol in diet"
      ],
      providerNextSteps: [
        "Strongly encourage patient to stop AAS use",
        "Strongly encourage patient to stop all non-prescribed aromatase inhibitors, SERMs, supplements, and non-prescribed over-the-counter medications",
        "Consider prescribing cholesterol lowering medications",
        "Recommend that patient decreases cholesterol in diet"
      ]
    },
    cardiacStressTest: {
      name: "Cardiac Stress Test",
      info: "Screening for evidence of coronary artery disease and strain on the heart with physical activity",
      abnormality: NO_RISK,
      risks: [
        "Coronary artery disease",
        "Heart strain"
      ],
      patientNextSteps: [
        "Strongly consider stopping AAS use",
        "Stop all non-prescribed supplements and non-prescribed over-the-counter medications",
        "Discuss with doctor about additional testing, cardiology referral, and initiation of cholesterol medications"
      ],
      providerNextSteps: [
        "Strongly encourage patient to stop AAS use",
        "Strongly encourage patient to stop all non-prescribed supplements and non-prescribed over-the-counter medications",
        "Perform additional testing, cardiology referral, and initiation of cholesterol medications if needed"
      ]
    },
    dexa: {
      name: "DEXA",
      info: "Screening for bone density",
      abnormality: NO_RISK,
      risks: [
        "Decreased bone density"
      ],
      patientNextSteps: [
        "Stop SERMs and aromatase inhibitors"
      ],
      providerNextSteps: [
        "Strongly encourage patient to stop SERMs and aromatase inhibitors"
      ]
    },
    ekg: {
      name: "EKG",
      info: "Screening for abnormal electrical activity, heart strain/overgrowth, heart muscle damage/prior heart attacks",
      abnormality: NO_RISK,
      risks: [
        "Cardiomegaly",
        "Abnormal electric activity",
        "Heart muscle damage",
        "Heart strain"
      ],
      patientNextSteps: [
        "Strongly consider stopping AAS use",
        "Stop all non-prescribed aromatase inhibitors, SERMs, supplements, and non-prescribed over-the-counter medications",
        "Discuss with doctor about additional testing and cardiology referral"
      ],
      providerNextSteps: [
        "Strongly encourage patient to stop AAS use",
        "Strongly encourage patient to stop non-prescribed aromatase inhibitors, SERMs, supplements, and non-prescribed over-the-counter medications",
        "Perform additional testing and cardiology referral if needed"
      ]
    },
    freeTestosterone: {
      name: "Free Testosterone",
      info: "Measures free testosterone available for function",
      abnormality: NO_RISK,
      risks: ["Levels will likely be elevated while using steroids and low immediately after using steroids."],
      patientNextSteps: [
        "If values are low after stopping all steroid use, discuss risk with provider "
      ],
      providerNextSteps: [
        "Monitor for secondary hypogonadism."
      ]
    },
    hemoglobin: {
      name: "Hemoglobin A1c",
      info: "Screening for average blood sugar, risk of diabetes or low blood sugar",
      abnormality: NO_RISK,
      risks: [
        "Diabetes",
        "High or low blood sugar"
      ],
      patientNextSteps: [
        "If low, stop all non-prescribed insulin",
        "If high, stop all HGH and discuss nutrition options with doctor or nutritionist"
      ],
      providerNextSteps: [
        "If low, recommend that patient stops all non-prescribed insulin",
        "If high, recommend that patient stops all HGH and discuss nutrition options"
      ]
    },
    lft: {
      name: "Liver Function Test",
      info: "Screening for liver damage",
      abnormality: NO_RISK,
      risks: [
        "Liver dysfunction"
      ],
      patientNextSteps: [
        "Stop all oral steroids, non-prescribed supplements, and non-prescribed over-the-counter medications",
        "Monitor for any new symptoms, especially abdominal distension, yellowing of eyes or skin, changes in color of stool or urine, bleeding or bruising",
        "Caution against consuming alcohol and other drugs"
      ],
      providerNextSteps: [
        "Encourage patient to stop all oral steroids, non-prescribed supplements, and non-prescribed over-the-counter medications",
        "Monitor for any new symptoms, especially abdominal distension, yellowing of eyes or skin, changes in color of stool or urine, bleeding or bruising",
        "Caution patient against consuming alcohol and other drugs"
      ]
    },
    psa: {
      name: "Prostate Specific Antigen",
      info: "Screening for prostate growth",
      abnormality: NO_RISK,
      risks: [
        "Prostate growth",
        "Difficulty urinating or straining with urination",
        "Elevated prostate specific antigen levels"
      ],
      patientNextSteps: [
        "Stop AAS if personal history or family history of prostate cancer",
        "See your provider for prostate screening and medications, such as tamsulosin, to help with benign prostate hypertrophy (BPH)",
        "Monitor for any new symptoms, especially or if having any difficulty or changes with urination",
        "Continue to monitor annually",
        "Consider decreased AAS use"
      ],
      providerNextSteps: [
        "Strongly encourage patient to stop AAS use if they have a personal history or family history of prostate cancer",
        "Prostate screening and medications, such as tamsulosin, to help with benign prostate hypertrophy (BPH)",
        "Monitor for any new symptoms, especially or if having any difficulty or changes with urination",
        "Continue to monitor annually",
        "Encourage patient to decrease AAS use"
      ]
    },
    semenalysis: {
      name: "Semenalysis",
      info: "Screening for infertility",
      abnormality: NO_RISK,
      risks: [
        "Infertility"
      ],
      patientNextSteps: [
        "If fertility is desired, stop all non-prescribed treatments"
      ],
      providerNextSteps: [
        "If fertility is desired, recommend that patient stops all non-prescribed treatments"
      ]
    },
    tte: {
      name: "Transthoracic Echocardiogram",
      info: "Screening for heart failure and abnormal heart size",
      abnormality: NO_RISK,
      risks: [
        "Heart failure"
      ],
      patientNextSteps: [
        "Stop all non-prescribed steroids, aromatase inhibitors, SERMs, supplements, and over-the counter medications",
        "Discuss with doctor about additional testing and cardiology referral"
      ],
      providerNextSteps: [
        "Encourage patient to stop all non-prescribed steroids, aromatase inhibitors, SERMs, supplements, and over-the-counter medications",
        "Perform additional testing and cardiology referral"
      ]
    },
    lipid: {
      name: "Lipid Panel",
      info: "Screening for cholesterol levels and risk of heart disease",
      abnormality: NO_RISK,
      risks: [
        "High cholesterol",
        "Heart disease risk"
      ],
      patientNextSteps: [
        "Strongly consider stopping AAS use",
        "Strongly consider stopping all non-prescribed aromatase inhibitors, SERMs, supplements, and non-prescribed over-the-counter medications",
        "Discuss with your doctor about starting cholesterol-lowering medications",
        "Decrease cholesterol in diet"
      ],
      providerNextSteps: [
        "Strongly encourage patient to stop AAS use",
        "Strongly encourage patient to stop all non-prescribed aromatase inhibitors, SERMs, supplements, and non-prescribed over-the-counter medications",
        "Consider prescribing cholesterol lowering medications",
        "Recommend that patient decreases cholesterol in diet"
      ]
    },
    cbc: {
      name: "Complete Blood Count",
      info: "Screening for elevated blood levels",
      abnormality: NO_RISK,
      risks: [
        "Elevated blood levels, which leads to risk of stroke"
      ],
      patientNextSteps: [
        "Stop or significantly reduce any AAS use",
        "Consider donating blood (if eligible)",
        "Monitor for any new symptoms, especially headaches, weakness, numbness, and shortness of breath"
      ],
      providerNextSteps: [
        "Encourage patient to stop or significantly reduce any AAS use",
        "Recommend that patient considers donating blood (if eligible)",
        "Monitor for any new symptoms, especially headaches, weakness, numbness, and shortness of breath"
      ]
    },
    hiv: {
      name: "HIV/Hep B/Hep C",
      info: "Screening for infection or immunity through vaccination",
      abnormality: NO_RISK,
      risks: [
        "HIV/HepB/HepC infection risk"
      ],
      patientNextSteps: [
        "Referral for HIV primary care",
        "Request Hep B vaccination if non-immune; if evidence of chronic infection, obtain referral for treatment",
        "Referral for Hep C treatment"
      ],
      providerNextSteps: [
        "Refer patient for HIV primary care",
        "Adminster Hep B vaccination if non-immune; if evidence of chronic infection, refer for treatment",
        "Refer patient for Hep C treatment"
      ]
    },
  }
};

/** Update state */
export const inc = (value, n) => {
  return Math.min(100, value + n);
}

export const dec = (value, n) => {
  return Math.max(0, value - n);
}

export const incBody = (body, n) => {
  return {
    ...body,
    abnormality: inc(body.abnormality, n)
  };
}

export const decBody = (body, n) => {
  return {
    ...body,
    abnormality: dec(body.abnormality, n)
  };
}

export const setLab = (lab, abnormality) => {
  return {
    ...lab,
    abnormality: abnormality
  };
}

export const incLab = (lab, n) => {
  return {
    ...lab,
    abnormality: inc(lab.abnormality, n)
  };
}

export const decLab = (lab, n) => {
  return {
    ...lab,
    abnormality: dec(lab.abnormality, n)
  };
}